import { useState } from 'react';
import { User, Trip, TemporaryResidencyPeriod, NotificationPreference, QuestionnaireResponse } from '../types';

export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // Get from local storage then parse stored json or return initialValue
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}

// Hook for managing complex app state with localStorage
export function useAppState() {
  const [user, setUser] = useLocalStorage<User | null>('citizenship-user', null);
  const [trips, setTrips] = useLocalStorage<Trip[]>('citizenship-trips', []);
  const [temporaryPeriods, setTemporaryPeriods] = useLocalStorage<TemporaryResidencyPeriod[]>('citizenship-temporary-periods', []);
  const [notificationPreferences, setNotificationPreferences] = useLocalStorage<NotificationPreference | null>('citizenship-notifications', null);
  const [questionnaireResponse, setQuestionnaireResponse] = useLocalStorage<QuestionnaireResponse | null>('citizenship-questionnaire', null);

  return {
    user,
    setUser,
    trips,
    setTrips,
    temporaryPeriods,
    setTemporaryPeriods,
    notificationPreferences,
    setNotificationPreferences,
    questionnaireResponse,
    setQuestionnaireResponse
  };
}
